// src/pages/reports/components/CallcenterUserSelectionModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { getCallcenterPerformanceUsers } from '../../../services/api/callcenterPerformance';
import { getCallcenterPerformanceSettings, saveCallcenterPerformanceSettings } from '../../../services/api/callcenterPerformanceSettings';
import { useLocalization } from '../../../contexts/LocalizationContext';
import SearchBar from '../../../components/common/SearchBar/SearchBar';
import styles from './CallcenterUserSelectionModal.module.css';

const CallcenterUserSelectionModal = ({ onClose, onSaved, onError }) => {
  const { t } = useLocalization();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getCallcenterPerformanceUsers(), getCallcenterPerformanceSettings()])
      .then(([usersRes, settings]) => {
        const list = usersRes.data || [];
        const ids = (settings.userIds || []).map(Number).filter(id => list.some(u => u.id === id));
        setUsers(list);
        setSelected(new Set(ids.length ? ids : list.map(u => u.id)));
      })
      .catch(() => onError(t('report.ccPerformanceUsers.loadFailed')))
      .finally(() => setLoadingUsers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? users.filter(u => u.name.toLowerCase().includes(term)) : users;
  }, [users, search]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const allSelected = selected.size === users.length;
      await saveCallcenterPerformanceSettings({ userIds: allSelected ? [] : [...selected] });
      onSaved();
    } catch (err) {
      onError(err?.response?.data?.message || t('report.ccPerformanceUsers.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('report.ccPerformanceUsers.title')}</h3>
          <button className={styles.modalClose} onClick={onClose}><X size={18} /></button>
        </div>
        <p className={styles.modalHint}>{t('report.ccPerformanceUsers.hint')}</p>
        {loadingUsers ? (
          <div className={styles.loadingState}><span className={styles.spinnerDark} /></div>
        ) : (
          <>
            <SearchBar
              value={search}
              onChange={setSearch}
              onClear={() => setSearch('')}
              autoSearch={false}
              size="small"
              placeholder={t('report.ccPerformanceUsers.searchUsers')}
            />
            <div className={styles.modalBulk}>
              <button className={styles.linkBtn} onClick={() => setSelected(new Set(users.map(u => u.id)))}>
                {t('report.ccPerformanceUsers.selectAll')}
              </button>
              <button className={styles.linkBtn} onClick={() => setSelected(new Set())}>
                {t('report.ccPerformanceUsers.clearAll')}
              </button>
            </div>
            <div className={styles.userList}>
              {filteredUsers.map(u => (
                <div key={u.id} className={styles.userRow}>
                  <span>{u.name}</span>
                  <label className={styles.toggle}>
                    <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} />
                    <span className={styles.toggleSlider} />
                  </label>
                </div>
              ))}
              {!filteredUsers.length && (
                <p className={styles.noResults}>{t('report.ccPerformanceUsers.noUsersFound')}</p>
              )}
            </div>
          </>
        )}
        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose}>{t('common.cancel')}</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving || loadingUsers}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallcenterUserSelectionModal;
